import React, { useState } from 'react';
import { FiPackage, FiTruck, FiCheck, FiAlertTriangle, FiXCircle } from 'react-icons/fi';
import { submitSupplyChecks, submitEquipmentCheck } from '../services/api';

function SupplyCheckModal({ categories, equipment, onClose, onComplete }) {
  const [supplyStatuses, setSupplyStatuses] = useState({});
  const [equipmentChecks, setEquipmentChecks] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const todayStr = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  // --- Supply helpers ---

  const setSupplyStatus = (categoryId, status) => {
    setSupplyStatuses(prev => ({
      ...prev,
      [categoryId]: { ...prev[categoryId], status }
    }));
  };

  const setSupplyNotes = (categoryId, notes) => {
    setSupplyStatuses(prev => ({
      ...prev,
      [categoryId]: { ...prev[categoryId], notes }
    }));
  };

  // --- Equipment helpers ---

  const setChecklistItemStatus = (equipmentId, checklistItemId, status) => {
    setEquipmentChecks(prev => {
      const existing = prev[equipmentId] || { details: {}, notes: '' };
      return {
        ...prev,
        [equipmentId]: {
          ...existing,
          details: {
            ...existing.details,
            [checklistItemId]: {
              ...(existing.details[checklistItemId] || {}),
              status
            }
          }
        }
      };
    });
  };

  const setChecklistItemNotes = (equipmentId, checklistItemId, notes) => {
    setEquipmentChecks(prev => {
      const existing = prev[equipmentId] || { details: {}, notes: '' };
      return {
        ...prev,
        [equipmentId]: {
          ...existing,
          details: {
            ...existing.details,
            [checklistItemId]: {
              ...(existing.details[checklistItemId] || {}),
              notes
            }
          }
        }
      };
    });
  };

  const setEquipmentNotes = (equipmentId, notes) => {
    setEquipmentChecks(prev => {
      const existing = prev[equipmentId] || { details: {}, notes: '' };
      return {
        ...prev,
        [equipmentId]: { ...existing, notes }
      };
    });
  };

  const getEquipmentOverallStatus = (equip) => {
    const checks = equipmentChecks[equip.id];
    if (!checks || !checks.details) return null;
    const statuses = Object.values(checks.details).map(d => d.status).filter(Boolean);
    if (statuses.length === 0) return null;
    if (statuses.includes('urgent')) return 'urgent';
    if (statuses.includes('needs_attention')) return 'needs_attention';
    return 'good';
  };

  // --- Validation ---

  const allSuppliesChecked = categories.length === 0 || categories.every(
    cat => supplyStatuses[cat.id] && supplyStatuses[cat.id].status
  );

  const allEquipmentChecked = equipment.length === 0 || equipment.every(equip => {
    const checks = equipmentChecks[equip.id];
    if (!checks || !checks.details) return false;
    const allItems = equip.checklist_items || [];
    // Filter items based on what's due
    const items = allItems.filter(item => {
      const freq = item.frequency || 'weekly';
      if (equip.monthly_due) return true;
      if (equip.weekly_due) return freq === 'daily' || freq === 'weekly';
      if (equip.daily_due) return freq === 'daily';
      return true;
    });
    return items.length > 0 && items.every(
      item => checks.details[item.id] && checks.details[item.id].status
    );
  });

  const canSubmit = allSuppliesChecked && allEquipmentChecked && !submitting;

  // --- Submit ---

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      // Submit supply checks
      if (categories.length > 0) {
        const checks = categories.map(cat => ({
          category_id: cat.id,
          status: supplyStatuses[cat.id]?.status || 'good',
          notes: supplyStatuses[cat.id]?.notes || ''
        }));
        await submitSupplyChecks(checks);
      }

      // Submit equipment checks
      for (const equip of equipment) {
        const eqCheck = equipmentChecks[equip.id];
        if (!eqCheck) continue;

        const checkType = equip.daily_due ? 'daily' : equip.weekly_due ? 'weekly' : 'monthly';
        const details = Object.entries(eqCheck.details || {}).map(([itemId, data]) => ({
          checklist_item_id: parseInt(itemId, 10),
          status: data.status,
          notes: data.notes || ''
        }));

        await submitEquipmentCheck(equip.id, {
          check_type: checkType,
          details,
          notes: eqCheck.notes || '',
          overall_status: getEquipmentOverallStatus(equip) || 'good'
        });
      }

      if (onComplete) onComplete();
      else onClose();
    } catch (err) {
      console.error('Error submitting checks:', err);
      setSubmitting(false);
    }
  };

  // --- Status button renderer ---

  const renderStatusButton = (label, value, currentValue, color, onClick) => {
    const isSelected = currentValue === value;
    const iconMap = {
      good: <FiCheck style={{ marginRight: 4, verticalAlign: 'middle' }} size={12} />,
      low: <FiAlertTriangle style={{ marginRight: 4, verticalAlign: 'middle' }} size={12} />,
      out: <FiXCircle style={{ marginRight: 4, verticalAlign: 'middle' }} size={12} />,
      needs_attention: <FiAlertTriangle style={{ marginRight: 4, verticalAlign: 'middle' }} size={12} />,
      urgent: <FiXCircle style={{ marginRight: 4, verticalAlign: 'middle' }} size={12} />
    };

    return (
      <button
        key={value}
        onClick={onClick}
        style={{
          padding: '6px 12px',
          fontSize: '12px',
          fontWeight: 600,
          border: `2px solid ${color}`,
          borderRadius: '6px',
          cursor: 'pointer',
          backgroundColor: isSelected ? color : 'transparent',
          color: isSelected ? '#ffffff' : color,
          transition: 'all 0.15s ease',
          display: 'inline-flex',
          alignItems: 'center',
          whiteSpace: 'nowrap'
        }}
      >
        {iconMap[value]}
        {label}
      </button>
    );
  };

  // --- Render ---

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1000,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <div
        style={{
          backgroundColor: '#ffffff',
          maxWidth: '600px',
          width: '95%',
          maxHeight: '85vh',
          overflowY: 'auto',
          borderRadius: '12px',
          padding: '24px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
        }}
      >
        {/* Header */}
        <div style={{ marginBottom: '20px', textAlign: 'center' }}>
          <h2 style={{
            margin: '0 0 4px 0',
            color: '#92400e',
            fontSize: '22px',
            fontWeight: 700
          }}>
            Supply & Equipment Check
          </h2>
          <p style={{
            margin: 0,
            color: '#92400e',
            fontSize: '14px',
            opacity: 0.8
          }}>
            {todayStr}
          </p>
        </div>

        {/* Supplies Section */}
        {categories.length > 0 && (
          <div style={{ marginBottom: '24px' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '12px',
              paddingBottom: '8px',
              borderBottom: '2px solid #fde68a'
            }}>
              <FiPackage size={18} style={{ color: '#92400e' }} />
              <h3 style={{ margin: 0, color: '#92400e', fontSize: '16px', fontWeight: 600 }}>
                Supplies
              </h3>
            </div>

            {categories.map(cat => {
              const current = supplyStatuses[cat.id] || {};
              return (
                <div
                  key={cat.id}
                  style={{
                    backgroundColor: '#fffbeb',
                    borderRadius: '8px',
                    padding: '14px',
                    marginBottom: '10px'
                  }}
                >
                  <div style={{
                    fontWeight: 600,
                    fontSize: '14px',
                    color: '#1f2937',
                    marginBottom: '8px'
                  }}>
                    {cat.name}
                  </div>

                  {/* Status buttons */}
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                    {renderStatusButton('Good', 'good', current.status, '#10b981', () => setSupplyStatus(cat.id, 'good'))}
                    {renderStatusButton('Low', 'low', current.status, '#f59e0b', () => setSupplyStatus(cat.id, 'low'))}
                    {renderStatusButton('Out', 'out', current.status, '#ef4444', () => setSupplyStatus(cat.id, 'out'))}
                  </div>

                  {/* Notes input */}
                  <input
                    type="text"
                    placeholder="Notes (optional)"
                    value={current.notes || ''}
                    onChange={e => setSupplyNotes(cat.id, e.target.value)}
                    style={{
                      width: '100%',
                      padding: '6px 10px',
                      fontSize: '12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      outline: 'none',
                      boxSizing: 'border-box',
                      backgroundColor: '#ffffff'
                    }}
                  />

                  {/* Supplier info */}
                  {cat.supplier && (
                    <div style={{
                      marginTop: '8px',
                      fontSize: '11px',
                      color: '#6b7280',
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '8px',
                      alignItems: 'center'
                    }}>
                      {cat.supplier.contact_name && (
                        <span>{cat.supplier.contact_name}</span>
                      )}
                      {cat.supplier.phone && (
                        <a
                          href={`tel:${cat.supplier.phone}`}
                          style={{ color: '#2563eb', textDecoration: 'none' }}
                        >
                          {cat.supplier.phone}
                        </a>
                      )}
                      {cat.supplier.website && (
                        <a
                          href={cat.supplier.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: '#2563eb', textDecoration: 'none' }}
                        >
                          Website
                        </a>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Equipment Section */}
        {equipment.length > 0 && (
          <div style={{ marginBottom: '24px' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '12px',
              paddingBottom: '8px',
              borderBottom: '2px solid #fde68a'
            }}>
              <FiTruck size={18} style={{ color: '#92400e' }} />
              <h3 style={{ margin: 0, color: '#92400e', fontSize: '16px', fontWeight: 600 }}>
                Equipment & Vehicles
              </h3>
            </div>

            {equipment.map(equip => {
              const eqCheck = equipmentChecks[equip.id] || { details: {}, notes: '' };
              const overallStatus = getEquipmentOverallStatus(equip);
              const allChecklistItems = equip.checklist_items || [];
              // Filter items based on what's due: daily shows daily items, weekly shows daily+weekly, monthly shows all
              const checklistItems = allChecklistItems.filter(item => {
                const freq = item.frequency || 'weekly';
                if (equip.monthly_due) return true; // monthly due = show all
                if (equip.weekly_due) return freq === 'daily' || freq === 'weekly';
                if (equip.daily_due) return freq === 'daily';
                return true;
              });
              const checkLabel = equip.daily_due ? 'Daily' : equip.weekly_due ? 'Weekly' : equip.monthly_due ? 'Monthly' : '';

              const overallStatusColor = overallStatus === 'good'
                ? '#10b981'
                : overallStatus === 'needs_attention'
                  ? '#f59e0b'
                  : overallStatus === 'urgent'
                    ? '#ef4444'
                    : '#9ca3af';

              return (
                <div
                  key={equip.id}
                  style={{
                    backgroundColor: '#fffbeb',
                    borderRadius: '8px',
                    padding: '14px',
                    marginBottom: '10px'
                  }}
                >
                  {/* Equipment name + type badge */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '8px'
                  }}>
                    <span style={{
                      fontWeight: 600,
                      fontSize: '14px',
                      color: '#1f2937'
                    }}>
                      {equip.name}
                    </span>
                    <span style={{
                      fontSize: '10px',
                      fontWeight: 600,
                      padding: '2px 8px',
                      borderRadius: '9999px',
                      backgroundColor: '#e5e7eb',
                      color: '#4b5563',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}>
                      {equip.equipment_type}
                    </span>
                    {checkLabel && (
                      <span style={{
                        fontSize: '10px',
                        fontWeight: 600,
                        padding: '2px 8px',
                        borderRadius: '9999px',
                        backgroundColor: '#dbeafe',
                        color: '#1d4ed8'
                      }}>
                        {checkLabel} Check
                      </span>
                    )}
                    {overallStatus && (
                      <span style={{
                        fontSize: '10px',
                        fontWeight: 600,
                        padding: '2px 8px',
                        borderRadius: '9999px',
                        backgroundColor: overallStatusColor,
                        color: '#ffffff',
                        marginLeft: 'auto'
                      }}>
                        {overallStatus === 'good' ? 'Good' : overallStatus === 'needs_attention' ? 'Attention' : 'Urgent'}
                      </span>
                    )}
                  </div>

                  {/* Checklist items */}
                  {checklistItems.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '8px' }}>
                      {checklistItems.map(item => {
                        const itemCheck = (eqCheck.details && eqCheck.details[item.id]) || {};
                        return (
                          <div
                            key={item.id}
                            style={{
                              padding: '8px 10px',
                              backgroundColor: '#ffffff',
                              borderRadius: '6px',
                              border: '1px solid #e5e7eb'
                            }}
                          >
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              marginBottom: '6px'
                            }}>
                              <span style={{
                                fontSize: '13px',
                                color: '#374151',
                                flexShrink: 0,
                                marginRight: '8px'
                              }}>
                                {item.name}
                              </span>
                              <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
                                {renderStatusButton('Good', 'good', itemCheck.status, '#10b981',
                                  () => setChecklistItemStatus(equip.id, item.id, 'good')
                                )}
                                {renderStatusButton('Needs Attention', 'needs_attention', itemCheck.status, '#f59e0b',
                                  () => setChecklistItemStatus(equip.id, item.id, 'needs_attention')
                                )}
                                {renderStatusButton('Urgent', 'urgent', itemCheck.status, '#ef4444',
                                  () => setChecklistItemStatus(equip.id, item.id, 'urgent')
                                )}
                              </div>
                            </div>
                            <input
                              type="text"
                              placeholder="Notes for this item (optional)"
                              value={itemCheck.notes || ''}
                              onChange={e => setChecklistItemNotes(equip.id, item.id, e.target.value)}
                              style={{
                                width: '100%',
                                padding: '4px 8px',
                                fontSize: '11px',
                                border: '1px solid #e5e7eb',
                                borderRadius: '4px',
                                outline: 'none',
                                boxSizing: 'border-box',
                                backgroundColor: '#f9fafb',
                                color: '#6b7280'
                              }}
                            />
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Equipment notes */}
                  <input
                    type="text"
                    placeholder="Overall notes (optional)"
                    value={eqCheck.notes || ''}
                    onChange={e => setEquipmentNotes(equip.id, e.target.value)}
                    style={{
                      width: '100%',
                      padding: '6px 10px',
                      fontSize: '12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      outline: 'none',
                      boxSizing: 'border-box',
                      backgroundColor: '#ffffff'
                    }}
                  />
                </div>
              );
            })}
          </div>
        )}

        {/* Footer */}
        <div style={{
          borderTop: '2px solid #fde68a',
          paddingTop: '16px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '8px'
        }}>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            style={{
              width: '100%',
              padding: '12px 24px',
              fontSize: '15px',
              fontWeight: 600,
              border: 'none',
              borderRadius: '8px',
              cursor: canSubmit ? 'pointer' : 'not-allowed',
              backgroundColor: canSubmit ? '#2563eb' : '#93c5fd',
              color: '#ffffff',
              transition: 'background-color 0.15s ease',
              opacity: canSubmit ? 1 : 0.7
            }}
          >
            {submitting ? 'Submitting...' : 'Submit All'}
          </button>
          <button
            onClick={onClose}
            disabled={submitting}
            style={{
              width: '100%',
              padding: '10px 24px',
              fontSize: '14px',
              fontWeight: 500,
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              cursor: 'pointer',
              backgroundColor: 'transparent',
              color: '#6b7280',
              transition: 'background-color 0.15s ease'
            }}
          >
            Remind Me Later
          </button>
          <p style={{
            margin: 0,
            fontSize: '11px',
            color: '#9ca3af'
          }}>
            This will keep appearing until you complete all checks
          </p>
        </div>
      </div>
    </div>
  );
}

export default SupplyCheckModal;
